import React, { useCallback } from "react";
import { commitMutation, graphql, useFragment } from "react-relay";

import { faUserSlash } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";

import { warning } from "~/lib/prompts";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { environment } from "~/models";
import { Menu } from "~/ui/menu";
import toast from "~/ui/toast";

import type { NotAOneOnOne_note$key } from "~/__generated__/NotAOneOnOne_note.graphql";
import type { NotAOneOnOneSplitMutation } from "~/__generated__/NotAOneOnOneSplitMutation.graphql";

const noteFragment = graphql`
	fragment NotAOneOnOne_note on Note {
		id
	}
`;

const splitMutation = graphql`
	mutation NotAOneOnOneSplitMutation($input: SplitNoteInput!) {
		splitNote(input: $input) {
			clientMutationId
			ok
			note {
				id
			}
		}
	}
`;

export default function NotAOneOnOne({ note: noteKey }: { note: NotAOneOnOne_note$key }) {
	const note = useFragment(noteFragment, noteKey);

	const onSelect = useCallback(async () => {
		const res = await warning(
			<div>
				If this recurring event is not a 1-on-1, Fellow can mark it as a regular meeting. Notes for future occurrences
				of this event will only appear in the meetings section.
			</div>,
			{ title: "Is this not a 1-on-1?", yesProps: { variant: "destructive" }, yesText: "Not a 1-on-1" },
		);

		if (!res) return;

		dispatchAnalyticsTrack("[1on1s] split", { note_id: note.id }); // eslint-disable-line camelcase

		commitMutation<NotAOneOnOneSplitMutation>(environment, {
			mutation: splitMutation,
			variables: {
				input: {
					id: note.id,
					useForNewNotes: true,
				},
			},
			onCompleted: data => {
				const { splitNote } = data;
				if (splitNote && splitNote.ok) {
					toast.success(`Removed this meeting from its series.`);
				} else {
					toast.error(`Unable to remove this meeting from its series.`);
				}
			},
		});
	}, [note.id]);

	return (
		<Menu.Item icon={faUserSlash} onSelect={onSelect} data-test-id="note-header-dropdown-not1on1-item">
			Not a 1-on-1…
		</Menu.Item>
	);
}
